"use client"
import "./globals.css"
import { Box, StyledEngineProvider, SxProps, ThemeProvider, createTheme } from "@mui/material"
import AdminAppHeader from "@/components/layout-components/Header"
import { COLORS } from "@/utils/constants/theme/colors"
import AdminNavBar from "@/components/layout-components/AdminNavBar"
import "react-toastify/dist/ReactToastify.css"
import bgLogin from "../utils/assets/images/bgLogin.png"
import { QueryClient, QueryClientProvider } from "react-query"
import { usePathname } from "next/navigation"
import { ReactNode, useEffect, useLayoutEffect, useState } from "react"
import ThemeGenerator from "@/utils/ThemeGenerator"
import { ToastContainer } from "react-toastify"
import SideBarTrigger from "@/utils/assets/images/SideBarTrigger"
import { observer } from "mobx-react"
import AppStore from "@/store/AppStore"
import FirebaseSDK from "@/components/FirebaseSDK/FirebaseSDK"
import { EventLogger } from "@/utils/CommonFunctions"
import { HighlightInit } from "@highlight-run/next/client"
import { APP_ENVIRONMENT, DOWNTIME_MODE } from "@/globalConfig"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const CollapseSX: SxProps = {
  display: "flex",
  alignItems: "center",
  position: "relative",
  height: "100%",
  "&::after": {
    content: "''",
    position: "absolute",
    left: "50%",
    transform: "translate(-50%)",
    borderRight: "1px solid rgba(255, 255, 255, 0.50)",
    height: "100%",
  },
  "& > div": {
    position: "absolute",
    left: "50%",
    transform: "translate(-50%) rotate(180deg)",
    zIndex: 2,
    width: "3rem",
    height: "3rem",
    cursor: "pointer",
    "&.GetRotated": {
      transform: "translate(-50%)",
    },
  },
  "& svg": {
    width: "3rem",
    height: "3rem",
  },
}

const RootLayout = ({ children, modal }: { children: ReactNode; modal: any }) => {
  const pathname = usePathname()
  const [collapse, setCollapse] = useState(false)

  const themeJSON = ThemeGenerator("dark")

  const maintenanceMode = DOWNTIME_MODE ?? false

  const theme = createTheme(themeJSON)

  useEffect(() => {
    setCollapse(AppStore.sideBarCollapse)
  }, [])

  const onClickToggleCollapse = () => {
    AppStore.setSideBarCollapse(!collapse)
    setCollapse((x) => !x)
    EventLogger("SideBar_Toggle", {
      state: collapse,
    })
  }

  if (maintenanceMode) {
    return (
      <html lang="en">
        <body>
          <div
            style={{
              fontFamily: "Arial, sans-serif",
              textAlign: "center",
              padding: "50px",
              background: COLORS.adminAppBgColor,
              color: "#333",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                maxWidth: "600px",
                margin: "0 auto",
                background: COLORS.adminAppBgColorV2,
                padding: "40px",
                borderRadius: "10px",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
              }}
            >
              <h1 style={{ fontSize: "2rem", color: COLORS.adminAppTextPrimary }}>🚧 Maintenance in Progress</h1>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "1rem 0 0 0 ",
                }}
              >
                <p style={{ fontSize: "1rem", color: COLORS.adminAppTextPrimary, marginTop: 2 }}>
                  We`&#39;`re doing some quick maintenance. The website will be back shortly—thanks for your patience!
                </p>
                <p style={{ fontSize: "1rem", color: COLORS.adminAppTextPrimary }}>- Team SERGO</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    )
  }

  return (
    <>
      {APP_ENVIRONMENT !== "development" && (
        <HighlightInit
          projectId={"jdk03r7e"}
          serviceName="admin-internal"
          tracingOrigins
          manualStart={false}
          // disableSessionRecording
          environment={APP_ENVIRONMENT}
          consoleMethodsToRecord={["error"]}
          disableBackgroundRecording
          networkRecording={{
            enabled: true,
            recordHeadersAndBody: true,
            urlBlocklist: [],
          }}
        />
      )}
      <html>
        <head>
          <link rel="shortcut icon" href="/favicon.ico" />
          <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
        </head>
        <body>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <QueryClientProvider client={queryClient}>
                <FirebaseSDK />
                <>
                  {pathname !== "/login" ? (
                    <Box
                      sx={{
                        width: "100hw",
                        height: "100vh",
                        backgroundColor: COLORS.adminAppBgColor,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ backgroundColor: COLORS.adminAppBgColor }}>
                        <AdminAppHeader />
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          backgroundColor: COLORS.adminAppBgColor,
                          height: "calc(100vh - 100px)",
                          overflow: "hidden",
                          display: "grid",
                          gridTemplateColumns: collapse ? "6rem 3rem calc(100% - 9rem)" : "17.5rem 1rem calc(100% - 18.5rem)",
                          transition: "200ms",
                        }}
                      >
                        <Box
                          sx={{
                            height: "auto",
                            overflow: "auto",
                            position: "relative",
                          }}
                        >
                          <Box>
                            <AdminNavBar collapse={collapse} />
                          </Box>
                        </Box>
                        <Box sx={CollapseSX}>
                          <Box className={collapse ? "GetRotated" : ""} sx={{ position: "relative" }} onClick={onClickToggleCollapse}>
                            {collapse ? <SideBarTrigger /> : <SideBarTrigger />}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            height: "100%",
                            maxHeight: "auto",
                            overflow: "auto",
                            scrollbarWidth: "thin",
                            "& > div": {
                              overflow: "auto",
                              scrollbarWidth: "thin",
                              margin: 0,
                              paddingLeft: collapse ? "15px" : "2rem",
                              paddingRight: "2rem",
                              paddingBottom: "2rem",
                            },
                          }}
                        >
                          {children}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        backgroundImage: `url(${bgLogin.src})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "100% 100%",
                        width: "100hw",
                        height: "100vh",
                      }}
                    >
                      {children}
                    </Box>
                  )}

                  <ToastContainer
                    position="top-left"
                    autoClose={1000}
                    hideProgressBar
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="dark"
                    limit={1}
                  />
                  {modal}
                </>
              </QueryClientProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </body>
      </html>
    </>
  )
}

export default observer(RootLayout)
